import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchStore } from '@/stores/searchStore';
import { useSearchHistoryStore } from '@/stores/searchHistoryStore';
import { useMapStore } from '@/stores/mapStore';
import { useUIStore } from '@/stores/uiStore';
import { searchService } from '@/services/searchService';
import { SearchSuggestions } from './SearchSuggestions';
import { parseCoordinates, coordinatesToSearchResult } from '@/utils/coordinates';
import { debounce } from 'lodash-es';
import type { SearchResult } from '@/types';

export function SearchBox() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  // Trending-near-you results (ranking engine). Populated on focus with an empty
  // query; empty array renders no section (honest empty state).
  const [trending, setTrending] = useState<SearchResult[]>([]);
  // Name/place search backend (/search, /autocomplete via geocoding) can be
  // down while the geocoding data builds. When it errors we flip this so the
  // dropdown can show a clear, non-blocking notice instead of hanging silently.
  const [searchUnavailable, setSearchUnavailable] = useState(false);

  const {
    query,
    setQuery,
    setIsSearching,
    setResults,
    setSuggestions,
    showSuggestions,
    setShowSuggestions,
    suggestions,
    addRecentSearch,
    setSelectedResult,
    searchFocus,
  } = useSearchStore();

  // Local, persisted "Recent" history (per-item remove + clear-all). This powers
  // the Recent list shown when the box is focused and empty.
  const {
    history: recentSearches,
    addToHistory,
    removeFromHistory,
    clearHistory,
  } = useSearchHistoryStore();

  const { flyTo, viewport } = useMapStore();
  const { openPanel } = useUIStore();

  // Raw-coordinate detection: a coordinate go-to needs NO backend, so it always
  // works even while name search is unavailable. When the query parses as a
  // coordinate we surface a distinct "Go to …" action instead of autocompleting.
  const coordinate = useMemo(() => parseCoordinates(query), [query]);
  const coordinateResult = useMemo(
    () => (coordinate ? coordinatesToSearchResult(coordinate) : null),
    [coordinate]
  );

  // Debounced autocomplete
  const debouncedAutocomplete = useCallback(
    debounce(async (searchQuery: string) => {
      if (searchQuery.length < 2) {
        setSuggestions([]);
        return;
      }

      const location = searchFocus ?? viewport.center;
      try {
        const results = await searchService.autocomplete(searchQuery, location, 8);
        // Personalized re-rank of the candidate set; falls back to original order
        // internally on any error, so suggestions never visibly degrade.
        const ranked = await searchService.rerankResults(results, {
          query: searchQuery,
          location,
        });
        setSuggestions(ranked);
        // The backend answered, so name search is healthy again.
        setSearchUnavailable(false);
      } catch (error) {
        // Ignore cancelled requests (a newer keystroke superseded this one).
        if ((error as Error).message !== 'canceled') {
          console.error('Autocomplete error:', error);
          // Geocoding backend down/timed out: clear stale suggestions and flag
          // the unavailable state so the dropdown can guide the user to
          // coordinates instead of hanging.
          setSuggestions([]);
          setSearchUnavailable(true);
        }
      }
    }, 200),
    [searchFocus, viewport.center, setSuggestions]
  );

  useEffect(() => {
    // Coordinate queries never hit the backend — surface the go-to action only.
    if (coordinateResult) {
      debouncedAutocomplete.cancel();
      setSuggestions([]);
      setSearchUnavailable(false);
      if (isFocused) setShowSuggestions(true);
    } else if (query && isFocused) {
      debouncedAutocomplete(query);
      setShowSuggestions(true);
    } else {
      debouncedAutocomplete.cancel();
      setSearchUnavailable(false);
    }

    return () => {
      debouncedAutocomplete.cancel();
    };
  }, [query, isFocused, coordinateResult, debouncedAutocomplete, setShowSuggestions, setSuggestions]);

  // Fetch "trending near you" when the box is focused with an empty query. The
  // service returns [] on error / nothing-trending, so we never fabricate.
  useEffect(() => {
    if (!isFocused || query) return;
    let cancelled = false;
    const location = searchFocus ?? viewport.center;
    searchService.getTrending(location, 8).then((results) => {
      if (!cancelled) setTrending(results);
    });
    return () => {
      cancelled = true;
    };
  }, [isFocused, query, searchFocus, viewport.center]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Always open the panel on focus: it self-hides when it has no content
    // (no suggestions, no recents, no trending), so an empty box shows nothing.
    setShowSuggestions(true);
  };

  const handleBlur = () => {
    // Delay to allow click on suggestions
    setTimeout(() => {
      setIsFocused(false);
      setShowSuggestions(false);
    }, 200);
  };

  // Fly to a raw coordinate, drop a marker, and open its place card. Reuses the
  // normal search-result pipeline: setResults([…]) makes MapMarkers render a
  // pin, and the place card's "Directions" action already routes to the point.
  const goToCoordinate = useCallback(
    (result: SearchResult) => {
      setQuery(result.name);
      setSuggestions([]);
      setShowSuggestions(false);
      setResults([result]); // drops a marker via MapMarkers (results-driven)
      setSelectedResult(result);
      addRecentSearch(result);
      addToHistory(result);
      flyTo(result.coordinates, 16);
      openPanel('placeDetailsPanel');
    },
    [setQuery, setSuggestions, setShowSuggestions, setResults, setSelectedResult, addRecentSearch, addToHistory, flyTo, openPanel]
  );

  const handleSearch = async () => {
    if (!query.trim()) return;

    // Coordinate go-to works with no backend — handle it before any /search call.
    if (coordinateResult) {
      goToCoordinate(coordinateResult);
      return;
    }

    setIsSearching(true);
    setShowSuggestions(false);

    const trimmed = query.trim();
    const location = searchFocus ?? viewport.center;

    try {
      const raw = await searchService.search({
        query: trimmed,
        location,
        limit: 20,
      });

      // Personalized re-rank; falls back to the original order internally on any
      // failure, so results never visibly degrade.
      const results = await searchService.rerankResults(raw, {
        query: trimmed,
        location,
      });

      setResults(results);

      // If we got results, fly to the first one
      if (results.length > 0) {
        const first = results[0];
        flyTo(first.coordinates, 15);
        setSelectedResult(first);
        addRecentSearch(first);
        addToHistory(first);
        // Feedback: the top result was surfaced + selected. Best-effort.
        searchService.recordClick({ result: first, query: trimmed, position: 0 });
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      inputRef.current?.blur();
    }
  };

  const handleSuggestionSelect = (result: SearchResult) => {
    // Best-effort feedback signals (fire-and-forget, never block/throw):
    //  - click: this result was chosen from the list.
    //  - dwell: it's being opened in the details panel (engagement).
    const position = suggestions.findIndex((s) => s.id === result.id);
    searchService.recordClick({
      result,
      query,
      position: position >= 0 ? position : 0,
    });
    searchService.recordDwell({ result, query });

    setQuery(result.name);
    setShowSuggestions(false);
    setSelectedResult(result);
    addRecentSearch(result);
    addToHistory(result);
    flyTo(result.coordinates, 16);
    openPanel('placeDetailsPanel');
  };

  // Re-open a Recent entry: re-run the exact pick (fly, mark, open details) so a
  // recent behaves like selecting it fresh. Also bumps it to the front.
  const handleRecentSelect = (result: SearchResult) => {
    setQuery(result.name || result.displayName);
    setSuggestions([]);
    setShowSuggestions(false);
    setResults([result]);
    setSelectedResult(result);
    addToHistory(result);
    flyTo(result.coordinates, 16);
    openPanel('placeDetailsPanel');
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setResults([]);
    setSelectedResult(null);
    inputRef.current?.focus();
  };

  const handleDirections = () => {
    openPanel('directionsPanel');
  };

  return (
    <div className="relative w-full max-w-lg">
      <div className="relative flex items-center bg-white rounded-lg shadow-card hover:shadow-card-hover transition-shadow">
        {/* Search icon */}
        <div className="pl-4 pr-2">
          <SearchIcon className="w-5 h-5 text-gray-500" />
        </div>

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="Search TensorMaps"
          className="flex-1 py-3 pr-2 text-gray-900 placeholder-gray-500 bg-transparent outline-none"
        />

        {/* Clear button */}
        {query && (
          <button
            onClick={handleClear}
            aria-label="Clear search"
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        )}

        {/* Divider */}
        <div className="w-px h-6 bg-gray-300" />

        {/* Directions button */}
        <button
          onClick={handleDirections}
          className="p-3 text-primary-600 hover:text-primary-700 transition-colors"
          title="Get directions"
          aria-label="Get directions"
        >
          <DirectionsIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && (isFocused || suggestions.length > 0) && (
        <SearchSuggestions
          suggestions={suggestions}
          recentSearches={query ? [] : recentSearches}
          trending={query ? [] : trending}
          coordinateResult={coordinateResult}
          searchUnavailable={searchUnavailable}
          onSelect={handleSuggestionSelect}
          onSelectCoordinate={goToCoordinate}
          onSelectRecent={handleRecentSelect}
          onRemoveRecent={removeFromHistory}
          onClearRecents={clearHistory}
        />
      )}
    </div>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function DirectionsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M22.43 10.59l-9.01-9.01c-.75-.75-2.07-.76-2.83 0l-9 9c-.78.78-.78 2.04 0 2.82l9 9c.39.39.9.58 1.41.58.51 0 1.02-.19 1.41-.58l9.01-9.01c.78-.78.78-2.04.01-2.8zM12.5 18v-4h-2v-2h2V8l4 4-4 4z" />
    </svg>
  );
}
